import React from 'react';
import { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid2 from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
// import { mainListItems, secondaryListItems } from './listItems';
import { DashboardService } from 'core/api/services/dashboard';
import DailyChart from './DailyChart';
import TotalChart from './TotalChart';
import Deposits from './Deposits';
import Orders from './Orders';

import { CircularIndeterminate } from '@utriper/core-ui';

// Polling configuration
const POLL_INTERVAL = 4000; // Poll every 2 seconds
const MAX_POLL_ATTEMPTS = 60; // Maximum 60 attempts (2 minutes total)

// function Copyright() {
//   return (
//     <Typography variant="body2" color="textSecondary" align="center">
//       {'Copyright © '}
//       <Link color="inherit" href="https://mui.com/">
//         Your Website
//       </Link>{' '}
//       {new Date().getFullYear()}
//       {'.'}
//     </Typography>
//   );
// }

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
    },
    toolbar: {
        paddingRight: 24, // keep right padding when drawer closed
    },
    toolbarIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 8px',
        ...theme.mixins.toolbar,
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    menuButton: {
        marginRight: 36,
    },
    menuButtonHidden: {
        display: 'none',
    },
    title: {
        flexGrow: 1,
    },
    drawerPaper: {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    drawerPaperClose: {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
            width: theme.spacing(9),
        },
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    },
    fixedHeight: {
        height: 370,
    },
}));

export default function Dashboard() {
    const classes = useStyles();
    const fixedHeightPaper = clsx(classes.paper, classes.fixedHeight);

    const [todayData, setTodayData] = useState(undefined);
    const [recentDeposits, setRecentDeposits] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [totalCreatedBookings, setTotalCreatedBookings] = useState(0);
    const [totalCreatedTickets, setTotalCreatedTickets] = useState(0);
    const [totalBookings, setTotalBookings] = useState(0);
    const [totalTickets, setTotalTickets] = useState(0);
    const [loading, setLoading] = useState(true);
    const [reportId, setReportId] = useState(null);

    // Polling control
    const pollIntervalRef = useRef(null);
    const pollAttemptsRef = useRef(0);
    const isInitializedRef = useRef(false);

    const updateDashboardData = (data) => {
        if (data.todayData) {
            setTodayData(data.todayData);
        }
        if (data.recentDeposits) {
            setRecentDeposits(data.recentDeposits);
        }
        if (data.recentOrders) {
            setRecentOrders(data.recentOrders);
        }
        if (data.totalCreatedBookings) {
            setTotalCreatedBookings(data.totalCreatedBookings);
        }
        if (data.totalCreatedTickets) {
            setTotalCreatedTickets(data.totalCreatedTickets);
        }
        if (data.totalBookings) {
            setTotalBookings(data.totalBookings);
        }
        if (data.totalTickets) {
            setTotalTickets(data.totalTickets);
        }
    };

    const checkReportStatus = async () => {
        if (!reportId) return;

        try {
            pollAttemptsRef.current += 1;

            const { data, meta, errors } = await DashboardService.getReportId(reportId);

            if (errors && errors.length > 0) {
                console.error('Dashboard errors:', errors);
            }

            // Check if report is still loading
            if (meta?.loading === true) {
                // Still loading, continue polling if under max attempts
                if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
                    console.warn('Dashboard polling exceeded maximum attempts');
                    stopPolling();
                    setLoading(false);
                }
                // Otherwise, polling will continue via the interval
            } else {
                // Report is ready!
                updateDashboardData(data);
                stopPolling();
                setLoading(false);
            }
        } catch (error) {
            console.error('Error checking report status:', error);
            stopPolling();
            setLoading(false);
        }
    };

    const startPolling = () => {
        // Clear any existing interval
        stopPolling();

        // Reset attempts counter
        pollAttemptsRef.current = 0;

        // Start polling
        pollIntervalRef.current = setInterval(() => {
            checkReportStatus();
        }, POLL_INTERVAL);

        // Also check immediately
        checkReportStatus();
    };

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    const initializeDashboard = async () => {
        try {
            setLoading(true);
            const { data, meta } = await DashboardService.get();

            if (data.reportId) {
                setReportId(data.reportId);

                // If data is already ready (not loading), set it immediately
                if (meta?.loading === false && data) {
                    updateDashboardData(data);
                    setLoading(false);
                }
                // Otherwise, polling will start when reportId is set
            } else {
                console.error('No reportId received from dashboard');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error initializing dashboard:', error);
            setLoading(false);
        }
    };

    // Start polling when reportId changes
    useEffect(() => {
        if (reportId) {
            startPolling();
        }

        // Cleanup on unmount
        return () => {
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId]);

    // Initialize dashboard on mount
    useEffect(() => {
        // Prevent double initialization in React Strict Mode
        if (isInitializedRef.current) return;

        isInitializedRef.current = true;
        initializeDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {loading ? (
                <CircularIndeterminate />
            ) : (
                <div className={classes.root}>
                    <CssBaseline />
                    <main className={classes.content}>
                        {/* <div className={classes.appBarSpacer} /> */}
                        <Container maxWidth="xl" className={classes.container}>
                            <Grid2 container spacing={3}>
                                {/* Charts */}
                                <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                                    <Paper className={fixedHeightPaper}>
                                        <TotalChart todayData={todayData} totalBookings={totalBookings} totalTickets={totalTickets} />
                                    </Paper>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                                    <Paper className={fixedHeightPaper}>
                                        <TotalChart created={true} name="Total Created Tickets" todayData={todayData} totalBookings={totalCreatedBookings} totalTickets={totalCreatedTickets} />
                                    </Paper>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 8, lg: 9 }}>
                                    <Paper className={fixedHeightPaper}>
                                        <DailyChart todayData={todayData} />
                                    </Paper>
                                </Grid2>
                                {/* Recent Deposits */}
                                <Grid2 size={{ xs: 12, md: 4, lg: 3 }}>
                                    <Paper className={fixedHeightPaper}>
                                        <Deposits recentDeposits={recentDeposits} />
                                    </Paper>
                                </Grid2>
                                {/* Recent Orders */}
                                <Grid2 size={{ xs: 12 }}>
                                    <Paper className={classes.paper}>
                                        <Orders recentOrders={recentOrders} />
                                    </Paper>
                                </Grid2>
                            </Grid2>
                            <Box pt={4}>{/* <Copyright /> */}</Box>
                        </Container>
                    </main>
                </div>
            )}
        </>
    );
}
